'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

interface NewsItem {
  id: number;
  num: string;
  date: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  href: string;
  isNew?: boolean;
}

const newsItems: NewsItem[] = [
{
  id: 1,
  num: '01 / 04',
  date: 'March 2, 2026',
  title: 'UNOC IS RECRUITING: HEAD CRUDE OIL MARKETING AND SUPPLY',
  description: 'UNOC seeks a seasoned professional to lead crude oil marketing and supply operations as Uganda transitions to first oil production.',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM.jpeg',
  imageAlt: 'UNOC leadership team at 10th anniversary event Uganda petroleum sector milestone celebration',
  href: '/news/unoc-recruiting-head-crude-oil-marketing-supply',
  isNew: true
},
{
  id: 2,
  num: '02 / 04',
  date: 'March 2, 2026',
  title: 'UNOC Strengthens Regional Energy Security with Strategic Partnership',
  description: 'Strategic collaborations with regional partners to enhance energy infrastructure and security across East Africa.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_182cf65df-1772643854633.png",
  imageAlt: 'UNOC and Baker Hughes EHO Ltd MoU signing ceremony for technical capabilities and Center of Excellence',
  href: '/news/unoc-strengthens-regional-energy-security'
},
{
  id: 3,
  num: '03 / 04',
  date: 'December 22, 2025',
  title: "UNOC Celebrated 10 Years Of Driving Uganda's Transformation In The Petroleum Sector",
  description: 'A decade of excellence in petroleum sector management, from inception to becoming a fully-fledged national oil company.',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM-2.jpeg',
  imageAlt: 'UNOC 10 year anniversary celebration with staff and government officials at petroleum sector event Uganda',
  href: '/news/unoc-celebrated-10-years-petroleum-sector'
},
{
  id: 4,
  num: '04 / 04',
  date: 'October 23, 2025',
  title: 'UNOC and Baker Hughes EHO Ltd Sign MoU to Build Technical Capacity',
  description: 'Partnership agreement to enhance technical capabilities and knowledge transfer in petroleum operations and management.',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/10/UNOC-BakerHughes.jpg',
  imageAlt: 'UNOC and Baker Hughes representatives at MoU signing for technical capacity building petroleum Uganda',
  href: '/news/unoc-baker-hughes-mou-technical-capacity'
}];


export default function NewsSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="news" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div
          className="mb-16 flex flex-wrap items-end justify-between gap-6"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease'
          }}>
          
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 05 ]</span>
              <div className="h-[1px] w-12 bg-[#CC0000]" />
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]" style={{ fontFamily: 'Inter, sans-serif' }}>Latest Updates</span>
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0F0F] leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.025em' }}>
              
              News & <span className="text-[#CC0000]">Media</span>
            </h2>
            <p className="text-lg text-gray-600 mt-5" style={{ fontFamily: 'Inter, sans-serif' }}>
              Latest updates and announcements from UNOC.
            </p>
          </div>
          <Link
            href="/news"
            className="inline-flex items-center gap-2 bg-[#CC0000] text-white pl-5 pr-2 py-2.5 rounded-sm text-sm font-semibold hover:bg-[#AA0000] transition-colors group">
            
            View All Media
            <span className="bg-white/15 group-hover:bg-white/25 transition-colors p-1.5 rounded-sm">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 7h10v10" />
                <path d="M7 17 17 7" />
              </svg>
            </span>
          </Link>
        </div>

        {/* News grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-gray-200">
          {newsItems.map((item, i) =>
          <Link
            key={item.id}
            href={item.href}
            className="bg-white border-r border-b border-gray-200 group cursor-pointer flex flex-col"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'none' : 'translateY(20px)',
              transition: `opacity 0.6s ease ${0.1 * (i + 1)}s, transform 0.6s ease ${0.1 * (i + 1)}s`
            }}>
            
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                src={item.image}
                alt={item.imageAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-[#0F0F0F]/0 group-hover:bg-[#0F0F0F]/20 transition-colors duration-300" />
                {item.isNew &&
              <span className="absolute top-4 left-4 bg-[#CC0000] text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm">
                    New
                  </span>
              }
                <span className="absolute top-4 right-4 font-mono text-[10px] text-white bg-black/40 backdrop-blur-sm px-2 py-1 tracking-[0.15em] rounded-sm">
                  {item.num}
                </span>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <p className="font-mono text-[10px] text-gray-500 tracking-[0.15em] uppercase mb-3">
                  {item.date}
                </p>
                <h3
                className="text-base lg:text-lg font-extrabold text-[#0F0F0F] leading-snug mb-3 group-hover:text-[#CC0000] transition-colors flex-1"
                style={{ fontFamily: 'Inter, sans-serif' }}>
                
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-5" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {item.description}
                </p>
                <div className="flex items-center gap-2 text-[#CC0000] text-sm font-semibold">
                  <span>Read more</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-1 transition-transform" aria-hidden="true">
                    <path d="M7 7h10v10" />
                    <path d="M7 17 17 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>);

}